<template>
  <div class="z-0">
    <div class="flex">
      <div class="w-84 bg-gray-100 z-20 fixed h-full"></div>
      <BoardSideBar class="w-84 z-20 bg-gray-100 absolute left-0" />
      <BoardTimeLine class="flex-1 flex" />
      <LegDetail
        v-if="legDetailActive"
        :selected-leg="selectedLeg"
        :isActive="legDetailActive"
        @legDetailClosed="closeSplitScreen"
      />
      <FleetDetail
        v-if="fleetDetailActive"
        :fleetMember="selectedAffiliation"
        :isActive="fleetDetailActive"
        @fleetDetailClosed="closeSplitScreen"
      />
      <FleetActionReminder
        v-if="fleetActionActive"
        :fleetAction="selectedFleetAction"
        :isActive="fleetActionActive"
        @fleetDetailClosed="closeSplitScreen"
      />
      <SubcontractorDetail
        v-if="subcontractorDetailActive"
        :subcontractor="selectedAffiliation"
        :isActive="subcontractorDetailActive"
        @close="closeSplitScreen"
      />
      <Dialog
        v-if="dialog.isActive"
        :title="dialog.title"
        :message="dialog.message"
        :isActive="dialog.isActive"
        :isCentered="dialog.isCentered"
        :showButtons="dialog.showButtons"
        :isCloseable="dialog.isCloseable"
        @confirm="confirmLostConnectionDialog"
      />
    </div>
    <LoadingScreen />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, provide, computed, reactive, watch } from "vue";
import BoardSideBar from "@/components/Board/SideBar/SideBar.vue";
import BoardTimeLine from "@/components/Board/TimeLine/TimeLine.vue";
import Dialog from "@/components/Dialog.vue";
import LegDetail from "@/components/Board/Leg/LegDetail/LegDetail.vue";
import FleetDetail from "@/components/Board/Fleet/FleetDetail/FleetDetail.vue";
import FleetActionReminder from "@/components/Board/Fleet/FleetAction/FleetAction.vue";
import SubcontractorDetail from "@/components/Board/Fleet/FleetDetail/SubcontractorDetail.vue";
import { BoardLeg } from "@/types/leg";
import { Truck, Driver, Trailer, Subcontractor, FleetEntityType, FleetEntity } from "@/types/fleet";
import { FleetAction, FleetActionPayload } from "@/types/action";
import LoadingScreen from "@/components/Board/LoadingScreen/LoadingScreen.vue";
import { useBrickStore } from "@/store/brick/BrickStore";
import { useFleetStore } from "@/store/fleet/FleetStore";
import { useBoardStore } from "@/store/board/BoardStore";

export default defineComponent({
  name: "Board",
  components: {
    BoardSideBar,
    BoardTimeLine,
    Dialog,
    LegDetail,
    FleetDetail,
    FleetActionReminder,
    SubcontractorDetail,
    LoadingScreen
  },
  props: {
    trucks: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const brickStore = useBrickStore();
    const fleetStore = useFleetStore();

    const selectedLeg = ref<BoardLeg | null>(null);
    const selectedAffiliation = ref<FleetEntity | null>(null);
    const selectedFleetAction = ref<FleetActionPayload | null>(null);

    const legDetailActive = ref(false);
    const fleetDetailActive = ref(false);
    const fleetActionActive = ref(false);
    const subcontractorDetailActive = ref(false);
    const dirtyLegId = computed<string | undefined>(() => brickStore.dirtyLegId);

    const showSplitScreen = (data: BoardLeg | FleetAction | FleetEntity) => {
      if (dirtyLegId.value != null) {
        return;
      }
      if ((data as Truck).type == FleetEntityType.TRUCK) {
        fleetDetailActive.value = true;
        selectedAffiliation.value = data as Truck;
      } else if ((data as Driver).type == FleetEntityType.DRIVER) {
        fleetDetailActive.value = true;
        selectedAffiliation.value = data as Driver;
      } else if ((data as Trailer).type == FleetEntityType.TRAILER) {
        fleetDetailActive.value = true;
        selectedAffiliation.value = data as Trailer;
      } else if ((data as Subcontractor).type == FleetEntityType.SUBCONTRACTOR) {
        subcontractorDetailActive.value = true;
        selectedAffiliation.value = data as Subcontractor;
      } else if ((data as FleetActionPayload).payloadType == "action") {
        selectedFleetAction.value = data as FleetActionPayload;
        fleetActionActive.value = true;
      } else {
        selectedLeg.value = data as BoardLeg;
        legDetailActive.value = true;
      }
      fleetStore.deactivateDriverCommentToggle();
    };

    const closeSplitScreen = () => {
      legDetailActive.value = false;
      fleetDetailActive.value = false;
      fleetActionActive.value = false;
      subcontractorDetailActive.value = false;
      selectedFleetAction.value = null;
      fleetStore.activateDriverCommentToggle();
    };

    provide("showSplitScreen", showSplitScreen);

    const dialog = reactive({
      isActive: false,
      showButtons: true,
      isCentered: true,
      title: "Lost connection",
      message: "Do you want to reload the board?",
      isCloseable: false
    });

    const showLostConnectionDialog = computed(() => useBoardStore().showLostConnectionDialog);

    const confirmLostConnectionDialog = () => {
      window.location.reload();
    };

    watch(
      () => showLostConnectionDialog.value,
      () => {
        dialog.isActive = showLostConnectionDialog.value;
      }
    );

    return {
      selectedLeg,
      legDetailActive,
      selectedAffiliation,
      fleetDetailActive,
      fleetActionActive,
      selectedFleetAction,
      subcontractorDetailActive,
      dialog,
      showSplitScreen,
      closeSplitScreen,
      confirmLostConnectionDialog
    };
  }
});
</script>

<style scoped></style>
